import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, DollarSign, Calendar as CalIcon } from "lucide-react";
import { PortalBookings } from "@/components/PortalBookings";
import { StarRating } from "@/components/StarRating";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const TIME_OPTIONS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"];

export default function PractitionerPortal() {
  const { user, loading, refresh } = useAuth();
  const [tab, setTab] = useState("profile");
  const [practitioner, setPractitioner] = useState(null);
  const [availability, setAvailability] = useState({});
  const [earnings, setEarnings] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("ACH");
  const [payoutDetails, setPayoutDetails] = useState("");

  const load = async () => {
    if (!user?.practitioner_id) return;
    const [p, a, e, h] = await Promise.all([
      api.get(`/practitioners/${user.practitioner_id}`),
      api.get(`/practitioners/${user.practitioner_id}/availability`),
      api.get("/cuddlers/me/earnings"),
      api.get("/cuddlers/me/payout-requests"),
    ]);
    setPractitioner(p.data);
    setAvailability(a.data.weekly_slots || {});
    setEarnings(e.data);
    setPayoutHistory(h.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="py-24 text-center text-[#A89896]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.practitioner_id) {
    return (
      <div className="max-w-xl mx-auto py-24 px-6 text-center" data-testid="portal-not-cuddler">
        <h1 className="font-serif text-4xl text-[#F7ECEB]">Practitioner portal.</h1>
        <p className="text-[#A89896] mt-4">This space is for approved cuddle therapists.</p>
        {user.role === "pending_cuddler" ? (
          <p className="mt-6 text-sm text-[#A89896]">Your application is under review. We'll notify you soon.</p>
        ) : (
          <Link to="/apply" className="btn-primary inline-block mt-6">Apply to become a cuddler</Link>
        )}
      </div>
    );
  }
  if (!practitioner) return <div className="py-24 text-center text-[#A89896]">Loading...</div>;

  const saveProfile = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setSaving(true);
    try {
      await api.put("/practitioners/me", {
        bio: practitioner.bio,
        hourly_rate: Number(practitioner.hourly_rate),
        location: practitioner.location,
        years_experience: Number(practitioner.years_experience),
        specialties: practitioner.specialties,
      });
      setMsg("Profile saved.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "public");
      const { data: uploaded } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const image_url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${uploaded.path}`;
      await api.put("/practitioners/me", { image_url });
      setPractitioner((p) => ({ ...p, image_url }));
      setMsg("Photo updated.");
      refresh();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleSlot = (day, time) => {
    setAvailability((a) => {
      const list = new Set(a[day] || []);
      if (list.has(time)) list.delete(time); else list.add(time);
      return { ...a, [day]: [...list].sort() };
    });
  };

  const saveAvailability = async () => {
    setErr(""); setMsg(""); setSaving(true);
    try {
      await api.put("/practitioners/me/availability", { weekly_slots: availability });
      setMsg("Availability saved.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const requestPayout = async () => {
    setErr(""); setMsg("");
    try {
      await api.post("/cuddlers/me/payout-request", {
        amount: Number(payoutAmount),
        method_preference: payoutMethod,
        payout_details: payoutDetails,
      });
      setMsg("Payout requested. Admin will process it shortly.");
      setPayoutAmount("");
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="practitioner-portal">
      <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B]">Practitioner portal</div>
      <h1 className="font-serif text-5xl mt-4 text-[#F7ECEB]">{practitioner.name}</h1>
      <p className="text-[#A89896] mt-2">Manage your profile, bookings, availability, and payouts.</p>
      <div className="mt-3"><StarRating value={practitioner.avg_rating} count={practitioner.review_count} testId="portal-rating" /></div>

      <div className="mt-10 flex flex-wrap gap-2">
        {[
          { k: "profile", label: "Profile" },
          { k: "bookings", label: "Bookings & Requests" },
          { k: "availability", label: "Availability" },
          { k: "earnings", label: "Earnings & Payouts" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`portal-tab-${t.k}`}
            className={`px-5 py-2 rounded-full text-sm border transition-colors ${
              tab === t.k ? "bg-[#C93B3B] text-white border-[#C93B3B]" : "bg-[#1F1717] text-[#F7ECEB] border-[#382A2A]"
            }`}>{t.label}</button>
        ))}
      </div>

      {msg && <div className="mt-4 text-sm text-[#C93B3B]" data-testid="portal-msg">{msg}</div>}
      {err && <div className="mt-4 text-sm text-red-700" data-testid="portal-err">{err}</div>}

      {tab === "profile" && (
        <form onSubmit={saveProfile} className="mt-8 tt-card p-8 grid md:grid-cols-3 gap-8" data-testid="portal-profile">
          <div>
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#110D0D]">
              {practitioner.image_url && <img src={practitioner.image_url} alt={practitioner.name} className="w-full h-full object-cover" />}
            </div>
            <label className="mt-4 flex items-center justify-center gap-2 border border-dashed border-[#382A2A] rounded-xl p-3 cursor-pointer text-sm text-[#C93B3B]">
              <Upload className="w-4 h-4" /> Replace photo
              <input type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="portal-photo-input" className="hidden" />
            </label>
          </div>
          <div className="md:col-span-2 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Bio</label>
              <textarea rows={5} value={practitioner.bio || ""} onChange={(e) => setPractitioner({ ...practitioner, bio: e.target.value })} data-testid="portal-bio"
                className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Hourly rate ($)</label>
                <input type="number" min={60} value={practitioner.hourly_rate} onChange={(e) => setPractitioner({ ...practitioner, hourly_rate: e.target.value })} data-testid="portal-rate"
                  className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Years experience</label>
                <input type="number" min={0} value={practitioner.years_experience} onChange={(e) => setPractitioner({ ...practitioner, years_experience: e.target.value })} data-testid="portal-years"
                  className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Location (city, state)</label>
              <input value={practitioner.location || ""} onChange={(e) => setPractitioner({ ...practitioner, location: e.target.value })} data-testid="portal-location"
                className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
            </div>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="portal-save-profile">
              {saving ? "Saving..." : "Save profile"}
            </button>
          </div>
        </form>
      )}

      {tab === "bookings" && <PortalBookings />}

      {tab === "availability" && (
        <div className="mt-8 tt-card p-8" data-testid="portal-availability">
          <div className="flex items-start gap-3 mb-6">
            <CalIcon className="w-5 h-5 text-[#C93B3B] mt-1" />
            <div>
              <div className="font-serif text-2xl text-[#F7ECEB]">Weekly schedule</div>
              <p className="text-sm text-[#A89896] mt-1">Members will only be able to book slots you turn on below. Uncheck a slot to close it.</p>
            </div>
          </div>
          <div className="space-y-4">
            {DAYS.map((d) => (
              <div key={d} className="border-b border-[#382A2A] pb-4">
                <div className="text-sm uppercase tracking-[0.2em] text-[#C93B3B] mb-3 capitalize">{d}</div>
                <div className="flex flex-wrap gap-2">
                  {TIME_OPTIONS.map((t) => {
                    const on = (availability[d] || []).includes(t);
                    return (
                      <button key={t} type="button" onClick={() => toggleSlot(d, t)}
                        data-testid={`slot-${d}-${t}`}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                          on ? "bg-[#C93B3B] text-white border-[#C93B3B]" : "bg-[#1F1717] text-[#F7ECEB] border-[#382A2A]"
                        }`}>{t}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <button onClick={saveAvailability} disabled={saving} className="btn-primary mt-6" data-testid="portal-save-availability">
            {saving ? "Saving..." : "Save availability"}
          </button>
        </div>
      )}

      {tab === "earnings" && earnings && (
        <div className="mt-8 grid lg:grid-cols-3 gap-6" data-testid="portal-earnings">
          <div className="tt-card p-6">
            <DollarSign className="w-6 h-6 text-[#C93B3B]" strokeWidth={1.4} />
            <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B] mt-4">Available to request</div>
            <div className="font-serif text-4xl text-[#F7ECEB] mt-2">${earnings.available.toFixed(2)}</div>
            <div className="mt-3 text-xs text-[#A89896] space-y-1">
              <div>Gross bookings: ${earnings.gross.toFixed(2)}</div>
              <div>Your 85% share: ${earnings.cuddler_share.toFixed(2)}</div>
              <div>Paid out: ${earnings.paid_out.toFixed(2)}</div>
              <div>Pending requests: ${earnings.pending_requests.toFixed(2)}</div>
              <div>Sessions: {earnings.session_count}</div>
            </div>
          </div>

          <div className="tt-card p-6 lg:col-span-2">
            <div className="font-serif text-2xl text-[#F7ECEB]">Request payout</div>
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Amount ($)</label>
                  <input type="number" step="0.01" min={1} value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} data-testid="payout-amount"
                    className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Method</label>
                  <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)} data-testid="payout-method"
                    className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]">
                    <option>ACH</option>
                    <option>Zelle</option>
                    <option>PayPal</option>
                    <option>Wire</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Payout details (account number, email, etc.)</label>
                <textarea rows={2} value={payoutDetails} onChange={(e) => setPayoutDetails(e.target.value)} data-testid="payout-details"
                  className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
              </div>
              <button onClick={requestPayout} className="btn-primary" data-testid="payout-submit">Request payout</button>
            </div>
          </div>

          <div className="lg:col-span-3 tt-card p-6">
            <div className="font-serif text-2xl text-[#F7ECEB] mb-4">Payout history</div>
            {payoutHistory.length === 0 && <div className="text-sm text-[#A89896]">No requests yet.</div>}
            <div className="space-y-3">
              {payoutHistory.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-[#382A2A] pb-3" data-testid={`history-${p.id}`}>
                  <div>
                    <div className="font-serif text-xl text-[#F7ECEB]">${p.amount.toFixed(2)}</div>
                    <div className="text-xs text-[#A89896]">{p.method_preference} · {p.created_at?.slice(0, 10)}</div>
                  </div>
                  <div className={`text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full ${
                    p.status === "paid" ? "bg-[#C93B3B] text-white" : "bg-[#110D0D] text-[#A89896]"
                  }`}>{p.status}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
